import { Injectable, inject } from '@angular/core';
import { ProjectMapManifestService } from './project-map-manifest.service';
import { AuthService } from './auth.service';

/**
 * The layers gate for design context (spec §10): the ArcGIS picker and the
 * `app-design-context` control exist only for a project with a published map
 * catalog. Cached per account + project (root services outlive logout — see
 * memory "root service caches are per-user").
 */
interface Availability { available: boolean; catalogRevision: number | null }

/** How long a "no published catalog" (404) or "no access" (403) answer is trusted. */
const NO_CATALOG_TTL_MS = 5 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class DesignContextAvailabilityService {
  private manifestApi = inject(ProjectMapManifestService);
  private auth = inject(AuthService);
  private cache = new Map<string, { answer: Promise<Availability>; expiresAt: number }>();

  private key(projectId: string): string {
    return `${this.auth.getCurrentUser()?.id || ''}:${projectId}`;
  }

  private lookup(projectId: string): Promise<Availability> {
    const key = this.key(projectId);
    const cached = this.cache.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.answer;
    const entry = { answer: null as Promise<Availability>, expiresAt: Infinity };
    entry.answer = this.manifestApi.manifest(projectId)
      .then(response => ({ available: !!response.manifest, catalogRevision: response.manifest?.catalogRevision ?? null }))
      .catch(error => {
        // Only touch our own entry, not a newer one set after invalidate().
        if (this.cache.get(key) === entry) {
          // No catalog (404) or no layer read for this account (403): remember it briefly so a
          // catalog published or a role changed mid-session is picked up.
          if (error?.status === 404 || error?.status === 403) entry.expiresAt = Date.now() + NO_CATALOG_TTL_MS;
          // Transient (network, timeout, 5xx): retry next time.
          else this.cache.delete(key);
        }
        return { available: false, catalogRevision: null };
      });
    this.cache.set(key, entry);
    return entry.answer;
  }

  async available(projectId: string): Promise<boolean> {
    if (!projectId) return false;
    return (await this.lookup(projectId)).available;
  }

  async catalogRevision(projectId: string): Promise<number | null> {
    if (!projectId) return null;
    return (await this.lookup(projectId)).catalogRevision;
  }

  invalidate(projectId?: string): void {
    if (!projectId) { this.cache.clear(); return; }
    this.cache.delete(this.key(projectId));
  }
}
